import {
  Box,
  Checkbox,
  Flex,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { Trans, useTranslation } from 'react-i18next';
import { useDashboardDaysStore } from 'src/models/useDashboardDaysModel';
import { getUtcTime } from 'src/utils/time';

import { ProvisionApi } from '@Apis';
import { useOrgStore } from '../../../../models/useOrgModel';
import { useUtcOffsetStore } from '../../../../models/useUtcOffsetModel';
import TableTemplate from '../../Charts/TableTemplate';
import DashboardCard, {
  DashboardCardDescription,
} from '../../Layout/DashboardCard';
import DashboardLayout from '../../Layout/DashboardLayout';
import TimeRangeSelector from '../../TimeRangeSelector';
import {
  atRiskUserCategoryTranslation,
  ADMIN_CONFIRMED_USER_COMPROMISED,
  SUSPICIOUS_CREDENTIAL_ACCESS,
  SUSPICIOUS_SIGN_IN,
  SUSPICIOUS_USER_BEHAVIOR,
} from './utils';
import { RiskUserTrends } from '@Dashboard/Charts/RiskUserTrends';
import {
  ConditionalAccessProvider,
  useConditionalAccess,
} from './ConditionalAccessContext';
import { ConditionalAccessSelector } from './ConditionalAccessSelector';
import { handleNavigate } from '@Utils/common';
import { useNavigate } from 'react-router-dom';
import { AppType } from '@Types/app';

const AtRiskUserTrends = (props: {
  atRiskUserTrending: Record<string, any>;
  day: string;
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();

  return (
    <DashboardCard
      title={t('riskUser.atRiskUserTrends')}
      column={2}
      height="273px"
    >
      <RiskUserTrends
        data={props.atRiskUserTrending?.[props.day]}
        interval={props.day}
      />
      <Flex mt="8px" direction="row" align="center" justify="center">
        <Box color={colorStyle.color.secondary}>
          <Trans i18nKey="riskUser.topHighRiskUser.desc.getMore">
            placeholder
            <Link
              textDecoration="underline"
              onClick={() => {
                window.open(
                  'https://portal-xdr.visionone.trendmicro.com/#/app/sase',
                  '_blank'
                );
              }}
            >
              placeholder
            </Link>
          </Trans>
        </Box>
      </Flex>
    </DashboardCard>
  );
};

const TopHighRiskUserList = (props: {
  topRiskUser: Record<string, any[]>;
  day: string;
  dataSource: {
    cas?: boolean;
    v1?: boolean;
    mip?: boolean;
  };
  mipEnabled: boolean;
}) => {
  return (
    <ConditionalAccessProvider
      mipEnabled={props.mipEnabled}
      dataSource={props.dataSource}
    >
      <TopHighRiskUserContent
        topRiskUser={props.topRiskUser}
        day={props.day}
        mipEnabled={props.mipEnabled}
        dataSource={props.dataSource}
      />
    </ConditionalAccessProvider>
  );
};

interface EnrichedUserData {
  userName: string;
  key: string;
  total: number;
  last_detected: string;
  currentPolicyValue: string;
  currentPolicyId?: string;
  mipEnabled: boolean;
  dataSource: {
    cas?: boolean;
    v1?: boolean;
    mip?: boolean;
  };
}

const TopHighRiskUserContent = (props: {
  topRiskUser: Record<string, any[]>;
  day: string;
  dataSource: {
    cas?: boolean;
    v1?: boolean;
    mip?: boolean;
  };
  mipEnabled: boolean;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const timeModel = useUtcOffsetStore();

  const { getUserPolicy, policies } = useConditionalAccess();

  // 预处理数据，为每个用户计算策略信息
  const enrichedUserData: EnrichedUserData[] = (
    props.topRiskUser[props.day] || []
  ).map((user: any) => {
    const currentPolicyValue = getUserPolicy(user.key);
    let currentPolicyId: string | undefined;

    if (currentPolicyValue) {
      const [action, app] = currentPolicyValue.split(':');
      const policy = policies.find(
        (p) =>
          p.action === action &&
          p.includeApp === app &&
          p.users.some((u) => u.userPrincipalName === user.key)
      );
      currentPolicyId = policy?.id;
    }

    return {
      ...user,
      currentPolicyValue,
      currentPolicyId,
      mipEnabled: props.mipEnabled,
    };
  });

  const config = [
    { width: '10%', grow: 1, align: 'left' },
    { width: '20%', grow: 1, align: 'left' },
    { width: '10%', grow: 1, align: 'right' },
    { width: '25%', grow: 1, align: 'left' },
    { width: '10%', grow: 1, align: 'left' },
  ];
  const header = [
    t('riskUser.topHighRiskUser.user'),
    t('riskUser.topHighRiskUser.emailAccount'),
    t('riskUser.topHighRiskUser.highRiskEvents'),
    t('riskUser.topHighRiskUser.conditionalAccessAction'),
    t('riskUser.topHighRiskUser.lastEventTime'),
  ];
  const data: any[] = enrichedUserData.map((x: any) => [
    x.userName || '',
    x.key || '',
    x.total || 0,
    <ConditionalAccessSelector
      userName={x.userName}
      userPrincipalName={x.key}
      currentPolicyId={x.currentPolicyId}
      currentPolicyValue={x.currentPolicyValue}
      mipEnabled={x.mipEnabled}
      dataSource={props.dataSource}
      onPolicyChange={(oldValue, newValue) => {
        console.log(`Policy changed for ${x.key}: ${oldValue} -> ${newValue}`);
      }}
    />,
    x.last_detected
      ? getUtcTime(
          x.last_detected,
          timeModel.offset,
          t('common.momentDateFormat')
        )
      : '',
  ]);

  return (
    <DashboardCard title={t('riskUser.topHighRiskUser.name')} column={2}>
      <DashboardCardDescription>
        <Text>
          <Trans
            i18nKey="riskUser.topHighRiskUser.desc.notification"
            ns="riskUser"
          >
            placeholder
            <Link
              onClick={() => {
                const path =
                  '/policy/global-settings/conditional-access-policies-for-risky-users';
                handleNavigate(path);
                navigate(path);
              }}
            >
              placeholder
            </Link>
          </Trans>
        </Text>
        <Text>
          <Trans i18nKey="riskUser.topHighRiskUser.desc.getMore">
            placeholder
            <Link
              href={'https://portal-xdr.visionone.trendmicro.com/#/app/sase'}
              target="_blank"
            >
              placeholder
            </Link>
          </Trans>
        </Text>
      </DashboardCardDescription>
      <TableTemplate config={config} header={header} data={data} />
    </DashboardCard>
  );
};

const TopHighRiskUserEvents = (props: {
  topRiskEvent: Record<string, any>;
  day: string;
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const timeModel = useUtcOffsetStore();

  const [category, setCategory] = useState<string>(SUSPICIOUS_SIGN_IN);

  const config = [
    { width: '10%', grow: 1, align: 'left' },
    { width: '10%', grow: 1, align: 'right' },
    { width: '10%', grow: 1, align: 'left' },
  ];
  const header = [
    t('riskUser.topHighRiskEvent.event'),
    t('riskUser.topHighRiskEvent.users'),
    t('riskUser.topHighRiskEvent.lastEventTime'),
  ];
  const data = (props.topRiskEvent?.[props.day]?.[category] || []).map(
    (x: any) => [
      x.key || '',
      x.total || 0,
      x.last_detected
        ? getUtcTime(
            x.last_detected,
            timeModel.offset,
            t('common.momentDateFormat')
          )
        : '',
    ]
  );

  return (
    <DashboardCard
      title={t('riskUser.topHighRiskEvent')}
      column={2}
      titleBar={
        <Menu>
          <MenuButton variant="ghost" color={colorStyle.color.secondary}>
            {t('riskUser.riskCategory')}: &nbsp;
            {atRiskUserCategoryTranslation(category)}
          </MenuButton>
          <MenuList>
            <MenuItem onClick={() => setCategory(SUSPICIOUS_SIGN_IN)}>
              {t('riskUser.suspiciousSignIn')}
            </MenuItem>
            <MenuItem onClick={() => setCategory(SUSPICIOUS_CREDENTIAL_ACCESS)}>
              {t('riskUser.suspiciousCredentialAccess')}
            </MenuItem>
            <MenuItem onClick={() => setCategory(SUSPICIOUS_USER_BEHAVIOR)}>
              {t('riskUser.suspiciousUserBehavior')}
            </MenuItem>
            <MenuItem
              onClick={() => setCategory(ADMIN_CONFIRMED_USER_COMPROMISED)}
            >
              {t('riskUser.adminConfirmedUserCompromised')}
            </MenuItem>
          </MenuList>
        </Menu>
      }
    >
      <TableTemplate config={config} header={header} data={data} />
    </DashboardCard>
  );
};

const RiskUserTrend = (props: { eventData: Record<string, any> }) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const orgModel = useOrgStore();
  const [cookies] = useCookies(['tenant_id']);
  const daysModel = useDashboardDaysStore();
  const day = daysModel.days['AtRiskUserTrends'];
  const v1Enabled = (cookies.tenant_id || '').toUpperCase() === 'TM';
  const [mipEnabled, setMipEnabled] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<Record<string, boolean>>({
    cas: true,
    v1: v1Enabled,
    mip: mipEnabled,
  });

  useEffect(() => {
    (async function doCall() {
      const resp = await ProvisionApi.getProvisionedServices(orgModel.orgId);
      // setServiceTypes(resp.data);
      setMipEnabled(resp.data.includes(AppType.IdentityProtection));
      setDataSource({
        ...dataSource,
        mip: resp.data.includes(AppType.IdentityProtection),
      });
    })();
  }, [orgModel.orgId]);

  return (
    <DashboardLayout
      title={t('riskUser.trend.title')}
      barColor="#5773D6"
      titleTips={
        <Flex
          height="100%"
          direction="row"
          align="center"
          color={colorStyle.color.secondary}
        >
          <Box>{t('riskUser.trend.dataSource')}:</Box>
          <Checkbox
            ml="24px"
            checked={dataSource.cas}
            onChange={() =>
              setDataSource({ ...dataSource, cas: !dataSource.cas })
            }
          >
            {t('riskUser.trend.cas')}
          </Checkbox>
          <Checkbox
            ml="24px"
            checked={dataSource.v1}
            disabled={!v1Enabled}
            onChange={() => {
              if (!v1Enabled) return;
              setDataSource({ ...dataSource, v1: !dataSource.v1 });
            }}
          >
            {t('riskUser.trend.v1')}
          </Checkbox>
          <Checkbox
            ml="24px"
            checked={dataSource.mip}
            disabled={!mipEnabled}
            onChange={() => {
              if (!mipEnabled) return;
              setDataSource({ ...dataSource, mip: !dataSource.mip });
            }}
          >
            {t('riskUser.trend.mip')}
          </Checkbox>
        </Flex>
      }
      titleBar={<TimeRangeSelector widgetId="AtRiskUserTrends" />}
    >
      <AtRiskUserTrends
        atRiskUserTrending={props.eventData?.atRiskUserTrending || {}}
        day={day}
      />
      <TopHighRiskUserList
        topRiskUser={props.eventData?.topRiskyUser || {}}
        day={day}
        dataSource={dataSource}
        mipEnabled={mipEnabled}
      />
      <TopHighRiskUserEvents
        topRiskEvent={props.eventData?.topRiskEvent || {}}
        day={day}
      />
    </DashboardLayout>
  );
};

export default RiskUserTrend;
